import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Trash2, Loader2, Video as VideoIcon } from "lucide-react";

const ACCEPT = "video/mp4,video/webm,video/quicktime";
const MAX_DURATION_SECONDS = 120; // 2 minutes — enforced client-side
const MAX_BYTES = 60 * 1024 * 1024; // 60 MB — matches server cap

/**
 * Coach intro-video manager (dashboard).
 * Enforces the 2-minute cap client-side by reading `HTMLVideoElement.duration`
 * on the file BEFORE upload — we don't have ffprobe on the pod, so this is
 * the fastest way to give the coach an immediate error instead of failing
 * silently after the whole file uploads.
 */
export default function IntroVideoManager({ coachEmail }) {
  const [intro, setIntro] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/api/coach/dashboard/${encodeURIComponent(coachEmail)}/intro-video`);
      setIntro(data.intro_video || null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't load intro video.");
    } finally {
      setLoading(false);
    }
  }, [coachEmail]);
  useEffect(() => { load(); }, [load]);

  const checkDuration = (file) =>
    new Promise((resolve) => {
      const el = document.createElement("video");
      el.preload = "metadata";
      el.src = URL.createObjectURL(file);
      el.onloadedmetadata = () => {
        URL.revokeObjectURL(el.src);
        resolve(el.duration || 0);
      };
      el.onerror = () => resolve(0);
    });

  const onFileChosen = async (files) => {
    const file = files?.[0];
    if (!file) return;
    if (file.size > MAX_BYTES) return toast.error("File is too large (max 60 MB).");
    const dur = await checkDuration(file);
    if (dur > MAX_DURATION_SECONDS + 1) {
      return toast.error(`Video is ${Math.round(dur)}s — intros must be ≤ 2 minutes.`);
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post(
        `/api/coach/dashboard/${encodeURIComponent(coachEmail)}/intro-video`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setIntro(data.intro_video);
      toast.success(intro ? "Intro video replaced." : "Intro video uploaded.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed.");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const remove = async () => {
    if (!window.confirm("Remove your intro video?")) return;
    try {
      await api.delete(`/api/coach/dashboard/${encodeURIComponent(coachEmail)}/intro-video`);
      setIntro(null);
      toast.success("Intro video removed.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't remove.");
    }
  };

  return (
    <section
      data-testid="intro-video-manager"
      className="mt-8 rounded-3xl border border-slate-200 bg-white p-6 md:p-8"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-display text-xl md:text-2xl tracking-tight text-slate-900">Intro video</h2>
          <p className="mt-1 text-[13.5px] text-slate-500 max-w-[52ch]">
            A short (≤ 2 min) intro appears at the top of your profile before your portfolio.
            Say hi, share your training style, and set expectations for a first session.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            data-testid="intro-video-upload-btn"
            className="btn-primary h-10 px-4 rounded-full text-[13px] font-semibold inline-flex items-center gap-2 disabled:opacity-40"
          >
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {intro ? "Replace" : "Upload video"}
          </button>
          {intro && (
            <button
              type="button"
              onClick={remove}
              data-testid="intro-video-delete-btn"
              className="h-10 w-10 rounded-full inline-flex items-center justify-center text-rose-600 hover:bg-rose-50"
              title="Delete intro video"
            ><Trash2 className="w-4 h-4" /></button>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPT}
            className="hidden"
            onChange={(e) => onFileChosen(e.target.files)}
            data-testid="intro-video-file-input"
          />
        </div>
      </div>

      <div className="mt-6">
        {loading ? (
          <div className="flex items-center justify-center py-10 text-slate-400">
            <Loader2 className="w-5 h-5 animate-spin" />
          </div>
        ) : intro ? (
          <div className="rounded-2xl overflow-hidden border border-slate-200 bg-black aspect-video max-w-[720px]">
            <video src={intro.url} controls preload="metadata" className="w-full h-full object-contain bg-black" data-testid="intro-video-preview" />
          </div>
        ) : (
          <div
            data-testid="intro-video-empty"
            className="rounded-2xl border border-dashed border-slate-200 p-10 text-center text-slate-500"
          >
            <VideoIcon className="w-6 h-6 mx-auto mb-3 opacity-60" />
            No intro video yet. Uploading one boosts profile conversion — clients love hearing you speak first.
          </div>
        )}
      </div>
    </section>
  );
}
